import { Component, Input, ViewChild } from '@angular/core';
import { AjaxService } from 'AjaxService';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { SpinnerService } from '@services/spinner.service';
import { EntityArt } from '@enums/entityArt.enum';
import DataSource from 'devextreme/data/data_source';
import { confirm } from 'devextreme/ui/dialog';
import { BEMERKUNG_VORLAGE_TEXT, BemerkungsBild } from './bemerkungen-html.model';
import { lastValueFrom } from 'rxjs';
import { fehlermeldung } from './bemerkungen-html.model';
import { bemerkungDrucken } from './bemerkungen-druck';

export interface IBemerkungenRequestViewModel {
  ownerId: number;
  entityArt: EntityArt;

  searchText: string;
  skip: number;
  take: number;
  sortColumn: string;
  sortDirection: boolean;
}

interface IBemerkungenResponseViewModel {
  items: IBemerkungenViewModel[];
  totalItems: number;
}

export interface IBemerkungenViewModel {
  id: number;
  ownerId: number;
  entityArt: EntityArt;

  benutzerName: string;
  erstellt: Date;
  betreff: string;
  text: string;
  textHtml?: string;
  rowVersion?: string;
  bilder?: BemerkungsBild[];
  gehoertZu: string;
  owi104?: boolean;
}

@Component({
  selector: 'cz-bemerkungen',
  templateUrl: 'bemerkungen.component.html',
  standalone: false,
})
export class BemerkungenComponent {
  @ViewChild('form', { static: false }) form: DxFormComponent;

  @Input() ownerId: number;
  @Input() entityArt: EntityArt;
  @Input() disabled: boolean = false;

  loading = false;
  dataSource: any = {};
  bearbeitung: IBemerkungenViewModel = null;
  aktionLaeuft = false;
  aktionStatus = '';
  aktionsfehler = '';
  loeschkonflikt = false;
  // Das Grid blendet Details beim Zuklappen nur aus; die Bildansicht muss dabei zerstört werden.
  geoeffneteBemerkungen = new Set<number>();

  onHtmlBearbeiten = e => this.bearbeiten(e.row.data);

  onHtmlDrucken = async e => {
    if (this.aktionLaeuft || this.bearbeitung) return;
    this.aktionsfehler = '';
    this.loeschkonflikt = false;
    this.aktionLaeuft = true;
    this.aktionStatus = 'Druckansicht wird vorbereitet …';
    try { await bemerkungDrucken(e.row.data, this.ajaxService); }
    catch (error) { this.aktionsfehler = fehlermeldung(error); }
    finally { this.aktionLaeuft = false; }
  };

  onHtmlLoeschen = async e => {
    if (this.disabled || this.bearbeitung || this.aktionLaeuft) return;
    const item: IBemerkungenViewModel = e.row.data;
    if (!await confirm('Diese Bemerkung einschließlich ihrer Bilder wirklich löschen?', 'Bemerkung löschen')) return;
    if (this.disabled || this.bearbeitung || this.aktionLaeuft) return;
    this.aktionsfehler = '';
    this.loeschkonflikt = false;
    this.aktionLaeuft = true;
    this.aktionStatus = 'Bemerkung wird gelöscht …';
    try {
      await lastValueFrom(this.ajaxService.post('api/Bemerkungen/DeleteHtml', { id: item.id, rowVersion: item.rowVersion }));
      await this.dataSource.reload();
    } catch (error) {
      this.loeschkonflikt = (error as any)?.status === 409;
      this.aktionsfehler = this.loeschkonflikt
        ? 'Die Bemerkung wurde zwischenzeitlich geändert. Bitte laden Sie die Liste neu und prüfen Sie den aktuellen Stand vor dem Löschen.'
        : fehlermeldung(error);
    } finally { this.aktionLaeuft = false; }
  };

  async listeNeuLaden() {
    if (this.aktionLaeuft) return;
    this.aktionLaeuft = true;
    this.aktionStatus = 'Bemerkungen werden geladen …';
    try {
      await this.dataSource.reload();
      this.aktionsfehler = '';
      this.loeschkonflikt = false;
    } catch (error) { this.aktionsfehler = fehlermeldung(error); }
    finally { this.aktionLaeuft = false; }
  }

  get istEinrichtung(): boolean {
    return [EntityArt.Einrichtung, EntityArt.HauptEinrichtung, EntityArt.UnterEinrichtung].includes(this.entityArt);
  }

  bearbeiten(item?: IBemerkungenViewModel) {
    if (this.disabled || this.bearbeitung || this.aktionLaeuft) return;
    this.aktionsfehler = '';
    this.loeschkonflikt = false;
    this.bearbeitung = item ? { ...item } : { id: 0, ownerId: this.ownerId, entityArt: this.entityArt, owi104: false } as IBemerkungenViewModel;
  }

  async htmlGespeichert() {
    this.bearbeitung = null;
    await this.dataSource.reload();
  }

  constructor(
    private ajaxService: AjaxService,
    private spinnerService: SpinnerService
  ) {
    const self = this;

    this.dataSource = new DataSource({
      load: function (loadOptions: any) {
        const searchRequest: IBemerkungenRequestViewModel = {
          entityArt: self.entityArt,
          ownerId: self.ownerId,
          searchText: loadOptions.filter && loadOptions.filter.length > 0 ? loadOptions.filter[0][2] : '',
          skip: loadOptions.skip,
          take: loadOptions.take,
          sortColumn: loadOptions.sort ? loadOptions.sort[0].selector : '',
          sortDirection: loadOptions.sort ? loadOptions.sort[0].desc : false,
        };

        return lastValueFrom(self.ajaxService.post<any>('api/Bemerkungen/Search', searchRequest)).then((response) => {
          return {
            data: response.items,
            totalCount: response.totalItems,
          };
        });
      },
      insert: function (rowData) {
        const bemerkung = {
          entityArt: self.entityArt,
          ownerId: self.ownerId,
          betreff: rowData.betreff,
          text: rowData.text,
          owi104: rowData.owi104,
        } as IBemerkungenViewModel;

        return lastValueFrom(self.ajaxService.post('api/Bemerkungen/Create', bemerkung)).then((response) => {
          self.dataSource.reload();
        });
      },
      update: function (key, rowData) {
        if (rowData) {
          if (rowData.betreff) {
            key.betreff = rowData.betreff;
          }
          if (Object.prototype.hasOwnProperty.call(rowData, 'text')) {
            key.text = rowData.text;
          }

          key.owi104 = rowData.owi104;
        }

        return lastValueFrom(self.ajaxService.post('api/Bemerkungen/Update', key))
          .then((response) => {
            self.dataSource.reload();
          })
          .catch((err) => {
            setTimeout(() => {
              throw err;
            }, 0);
          });
      },
    });
  }

  onClickPrint(data: IBemerkungenViewModel) {
    const downloadLink = document.createElement('a');
    const blob = new Blob([
      '\ufeff',
      'Erstellt: ' +
        new Date(data.erstellt).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' }) +
        '\r\n' +
        'Gehört zu: ' +
        data.gehoertZu +
        '\r\n\r\n' +
        'Betreff: ' +
        data.betreff +
        '\r\n\r\n' +
        data.text,
    ]);
    const url = URL.createObjectURL(blob);

    downloadLink.href = url;
    downloadLink.download = this.GetFilename(data);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  GetFilename(data: IBemerkungenViewModel) {
    const re = /\./gi;
    let fileName = new Date().toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(re, '_');
    fileName += '_' + EntityArt.shortKey(data.entityArt) + '_' + data.betreff + '.txt';
    return fileName;
  }

  onEditorPreparing(e) {
    if (e.dataField === 'text') {
      e.editorName = 'dxTextArea';
    }
  }

  onCellPrepared(e) {
    if (e.rowType !== 'data' || !['bemerkungAktionen', 'bemerkungDruck'].includes(e.column.name)) return;
    // DevExtreme setzt bei reinen Icons nur den Tooltip; Screenreader benötigen den Aktionsnamen.
    e.cellElement.querySelectorAll('a[title]').forEach((icon: HTMLAnchorElement) => {
      icon.setAttribute('aria-label', icon.title);
    });
  }

  onToolbarPreparing(e) {
    const toolbarItems = e.toolbarOptions.items;
    const searchPanel = toolbarItems.filter((item) => item.name === 'searchPanel');

    if (searchPanel && searchPanel[0]) {
      searchPanel[0].location = 'before';
    }
  }

  onInitNewRow(e) {
    e.data.owi104 = false;
    if (this.entityArt === EntityArt.Einrichtung) {
      e.data.text = BEMERKUNG_VORLAGE_TEXT;
    }
  }

  private startLoading() {
    this.spinnerService.show();
    this.loading = true;
  }

  private stopLoading() {
    this.spinnerService.hide();
    this.loading = false;
  }
}
